import { useContext } from "react";

import { ActionIcon, Badge, HoverCard, rem, Switch, Table, Text } from "@mantine/core";
import { modals } from "@mantine/modals";
import { IconTrash } from "@tabler/icons-react";
import dayjs from "dayjs";

import { TimezoneContext } from "../../App.tsx";
import { MAX_ELEMENTS_PER_PAGE } from "../../constants/constants.ts";
import { SenderStatus, SenderStatusProperties } from "../../enums/SenderStatus.ts";
import { Pageable } from "../../models/Pageable.ts";
import { Sender } from "../../models/Sender.ts";
import { useSenderMutation } from "../../mutations/useSenderMutation.ts";
import { TableSkeleton } from "../skeletons/TableSkeleton.tsx";

export function SendersTable({
  senders,
  isLoading
}: Readonly<{
  senders: Pageable<Sender>;
  isLoading: boolean;
}>) {
  const { removeSenderMutation, switchLogModeSenderMutation } = useSenderMutation();
  const browserTimezone = useContext(TimezoneContext);

  const openDeleteModal = (senderEmail: string) =>
    modals.openConfirmModal({
      title: "Warning",
      yOffset: rem(200),
      children: <Text size="sm">Are you sure you want to delete the sender ?</Text>,
      labels: { confirm: "Delete", cancel: "Cancel" },
      confirmProps: { color: "red" },
      onConfirm: () => removeSenderMutation.mutate(senderEmail)
    });

  const rows = senders?.content.map((sender) => (
    <Table.Tr key={sender.id}>
      <Table.Td>
        <Text size={"lg"}>{sender.email}</Text>
        <Text size={"sm"} style={{ color: "darkgray" }}>
          Last used{" "}
          {sender.lastUsed
            ? dayjs(
                dayjs(sender.lastUsed).tz(browserTimezone).format("YYYY-MM-DD HH:mm:ss")
              ).fromNow()
            : "never"}
        </Text>
      </Table.Td>
      <Table.Td style={{ textAlign: "center", verticalAlign: "middle" }}>
        {sender.senderStatus !== SenderStatus.DELETED ? (
          <HoverCard width={280} shadow="md" openDelay={500}>
            <HoverCard.Target>
              <div style={{ display: "inline-flex" }}>
                <Switch
                  size="md"
                  onLabel="ON"
                  offLabel="OFF"
                  color="brand.3"
                  checked={!sender.loggingMode}
                  onClick={() => switchLogModeSenderMutation.mutate(sender.id)}
                />
              </div>
            </HoverCard.Target>
            <HoverCard.Dropdown>
              {sender.loggingMode ? (
                <Text size="sm">Emails from this sender won't be sent to their target.</Text>
              ) : (
                <Text size="sm">Emails from this sender will be sent to their target.</Text>
              )}
            </HoverCard.Dropdown>
          </HoverCard>
        ) : null}
      </Table.Td>
      <Table.Td style={{ textAlign: "center", verticalAlign: "middle" }}>
        <HoverCard width={280} shadow="md" openDelay={500}>
          <HoverCard.Target>
            <Badge variant="light" color={SenderStatusProperties[sender.senderStatus].color}>
              {SenderStatusProperties[sender.senderStatus].value}
            </Badge>
          </HoverCard.Target>
          <HoverCard.Dropdown>
            <Text size="sm">{SenderStatusProperties[sender.senderStatus].description}</Text>
          </HoverCard.Dropdown>
        </HoverCard>
      </Table.Td>
      <Table.Td>
        {sender.senderStatus !== SenderStatus.DELETED ? (
          <ActionIcon onClick={() => openDeleteModal(sender.email)} variant="subtle" color="red">
            <IconTrash style={{ width: rem(24), height: rem(24) }} stroke={1.5} />
          </ActionIcon>
        ) : null}
      </Table.Td>
    </Table.Tr>
  ));

  return (
    <Table>
      <Table verticalSpacing="sm">
        <Table.Tbody>
          {isLoading ? (
            <TableSkeleton columnNumber={4} rowNumber={MAX_ELEMENTS_PER_PAGE} key="skeleton" />
          ) : (
            rows
          )}
        </Table.Tbody>
      </Table>
    </Table>
  );
}
